import React, { useEffect, useRef, useState } from 'react';

import { MainContainer } from '../../../components/MainContainer/MainContainer';
import { Loading } from '../../../components/Loading/Loading';
import { Pagination } from '../../../components/Pagination/Pagination';
import { ForbiddenError } from '../Errors/ForbiddenError';
import { useGetSupportOrdersQuery, useGetUserTypeQuery } from '../../../redux/services/SupportApi';
import { type SupportOrder } from '../../../redux/types/SupportTypes';
import { SUPPORT_COLUMNS, SupportTable } from './components/SupportTable';
import { DisputeModal } from './components/DisputeModal';
import { ResolveModal } from './components/ResolveModal';
import { STATUS_OPTIONS } from './helpers';
import styles from './Support.module.css';

const PAGE_SIZE = 25;
const COLUMNS_KEY = 'support-orders-columns';

const loadColumns = (): Record<string, boolean> => {
  try {
    const raw = window.localStorage.getItem(COLUMNS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const Support = () => {
  const { data: userType, isLoading: isTypeLoading } = useGetUserTypeQuery();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [merchant, setMerchant] = useState('');
  const [trader, setTrader] = useState('');
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState<Record<string, boolean>>(loadColumns);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef<HTMLDivElement | null>(null);
  const [disputeFor, setDisputeFor] = useState<SupportOrder | null>(null);
  const [resolveFor, setResolveFor] = useState<{ order: SupportOrder; decision: 'approve' | 'reject' } | null>(null);
  const isAdmin = userType === 'admin';

  const { data, isLoading, isFetching, isError, refetch } = useGetSupportOrdersQuery(
    { page, page_size: PAGE_SIZE, search: search.trim(), status, merchant: merchant.trim(), trader: trader.trim() },
    { skip: !isAdmin, pollingInterval: 20000 },
  );

  useEffect(() => setPage(1), [search, status, merchant, trader]);
  useEffect(() => {
    try {
      window.localStorage.setItem(COLUMNS_KEY, JSON.stringify(visible));
    } catch {
      /* per-browser convenience only */
    }
  }, [visible]);
  useEffect(() => {
    if (!columnsOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (!columnsRef.current?.contains(event.target as Node)) setColumnsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [columnsOpen]);

  if (isTypeLoading) return <Loading />;
  if (!isAdmin) return <ForbiddenError />;

  const totalPages = Math.max(1, Math.ceil((data?.count || 0) / PAGE_SIZE));

  return (
    <MainContainer>
      <div className={'cards'} style={{ height: '100%' }}>
        <div className={styles.wrapper}>
          <h1>Саппорт</h1>
          <p className={styles.hint}>
            Все заявки платформы. «Открыть спор» действует от лица мерчанта заявки, «Подтвердить» и «Отклонить» —
            от лица трейдера. В споре остаётся пометка, кто из саппорта его открыл.
          </p>
          <div className={styles.filters}>
            <input
              type='search'
              placeholder='ID заявки, ID у мерчанта или клиента'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <input placeholder='Мерчант' value={merchant} onChange={(event) => setMerchant(event.target.value)} />
            <input placeholder='Трейдер' value={trader} onChange={(event) => setTrader(event.target.value)} />
            <div className={styles.columnsWrap} ref={columnsRef}>
              <button type='button' className={styles.actionButton} onClick={() => setColumnsOpen(!columnsOpen)}>
                Столбцы
              </button>
              {columnsOpen && (
                <div className={styles.columnsMenu}>
                  <label>
                    <input
                      type='checkbox'
                      checked={SUPPORT_COLUMNS.every((column) => visible[column.key] !== false)}
                      onChange={(event) =>
                        setVisible(Object.fromEntries(SUPPORT_COLUMNS.map((column) => [column.key, event.target.checked])))
                      }
                    />
                    Все
                  </label>
                  {SUPPORT_COLUMNS.map((column) => (
                    <label key={column.key}>
                      <input
                        type='checkbox'
                        checked={visible[column.key] !== false}
                        onChange={(event) => setVisible({ ...visible, [column.key]: event.target.checked })}
                      />
                      {column.name}
                    </label>
                  ))}
                  <label>
                    <input type='checkbox' checked disabled /> Действия (всегда)
                  </label>
                </div>
              )}
            </div>
            <span className={styles.count}>
              {data ? `Найдено: ${data.count}` : ''}
              {isFetching ? ' · обновление…' : ''}
            </span>
          </div>
          {isError && (
            <div className={styles.error}>
              Не удалось загрузить заявки.{' '}
              <button type='button' className={styles.actionButton} onClick={() => refetch()}>
                Повторить
              </button>
            </div>
          )}
          {isLoading ? (
            <Loading />
          ) : (
            <SupportTable
              orders={data?.results || []}
              visible={visible}
              busy={Boolean(disputeFor || resolveFor)}
              onDispute={setDisputeFor}
              onResolve={(order, decision) => setResolveFor({ order, decision })}
            />
          )}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Pagination
                totalPages={totalPages}
                currentPage={page}
                setCurrentPage={setPage}
                nextPage={data?.next || page}
                previousPage={data?.previous || page}
              />
            </div>
          )}
          {disputeFor && <DisputeModal order={disputeFor} onClose={() => setDisputeFor(null)} />}
          {resolveFor && (
            <ResolveModal order={resolveFor.order} decision={resolveFor.decision} onClose={() => setResolveFor(null)} />
          )}
        </div>
      </div>
    </MainContainer>
  );
};
